/**
 * The one-time coach mark that introduces global search.
 *
 * Shows once, a beat after the page settles, anchored wherever the host
 * mounts it (the listing search bar). It retires FOREVER on either exit:
 * "Got it", or the palette being opened by any path — teaching a feature
 * someone already uses is nagging, so the palette itself marks the hint
 * seen on open.
 *
 * Frosted in the aip vocabulary to visually belong to the palette it
 * introduces.
 */

import { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { KbdChip } from '@/shared/components/KbdChip.jsx';
import {
  markSearchHintSeen,
  openCommandPalette,
  searchHintSeen,
} from '@/shared/utils/commandPalette.js';
import { shortcutLabel } from '@/shared/utils/platform.js';
import { cn } from '@/lib/utils';

const APPEAR_DELAY_MS = 1200;

export function SearchHintCallout({ className }) {
  const { t } = useTranslation(['search']);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (searchHintSeen()) return undefined;
    // A beat after mount, so the hint never competes with the page loading.
    const timer = setTimeout(() => {
      // Re-check: the user may have opened the palette in the meantime.
      if (!searchHintSeen()) setVisible(true);
    }, APPEAR_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    markSearchHintSeen();
    setVisible(false);
  };

  const tryIt = () => {
    // Opening the palette marks the hint seen — but do it here too, so the
    // teaching loop closes even if the palette is guarded on this route.
    markSearchHintSeen();
    setVisible(false);
    openCommandPalette();
  };

  return (
    <div
      role="note"
      aria-label={t('search:hintCallout.title')}
      className={cn(
        'zaip-panel-in w-72 rounded-[14px] bg-white/80 p-3 shadow-[0_8px_32px_rgba(0,0,0,0.14)]',
        'backdrop-blur-[16px] dark:bg-zinc-900/80',
        className,
      )}
    >
      {/* Caret pointing up at the keycap this callout explains. */}
      <div
        aria-hidden
        className="absolute -top-1 right-6 h-2.5 w-2.5 rotate-45 bg-white/80 dark:bg-zinc-900/80"
      />

      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-500/15">
          <Sparkles className="h-3.5 w-3.5 text-teal-600 dark:text-teal-300" />
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            {t('search:hintCallout.title')}
          </div>
          <p className="mt-0.5 text-xs leading-relaxed text-zinc-600 dark:text-zinc-300">
            {t('search:hintCallout.body')} <KbdChip>{shortcutLabel()}</KbdChip>
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={tryIt}
              className="rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 px-3 py-1 text-xs font-semibold text-white shadow-md transition active:scale-[0.98]"
            >
              {t('search:hintCallout.tryIt')}
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="rounded-full px-2 py-1 text-xs font-medium text-zinc-500 transition hover:text-zinc-700 dark:hover:text-zinc-300"
            >
              {t('search:hintCallout.gotIt')}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('search:hintCallout.gotIt')}
          className="shrink-0 rounded-full p-1 text-zinc-400 transition hover:text-zinc-600 dark:hover:text-zinc-200"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
